/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react'
import LayoutDefault from '@/components/layouts/Layout'
import SectionHeader from '@/components/modules/SectionHeader'
import { gsap, TimelineLite, Power3 } from 'gsap'
import PortfolioGridItem from '@/components/modules/PortfolioGridItem'
import classNames from 'classnames'
import { FaArrowRight } from 'react-icons/fa'
import {
  AllByCategory,
  AllCategory,
  getAllPortfolioForHome,
  getPostAndMorePosts,
} from '@/service/portfolio'
import { GetStaticProps } from 'next'
import { Category, Post, PostItem } from '@/types/portfolio'
import Button from '@/components/elements/Button'

interface PortfolioItemsProps {
  id: number
  title: string
  thumb: string
  category: string[]
  bgColor?: string
}

interface PortfolioCategoryItem {
  id: string
  name: string
  slug: string
}
interface PortfolioCategory {
  children: {
    nodes: PortfolioCategoryItem[]
  }
  id: string
  name: string
  slug: string
  hasLoadMore: boolean
}

const PortfolioPage = ({
  posts,
  allCategory,
  hasLoadMore,
}: {
  posts: Post
  allCategory: PortfolioCategory
  hasLoadMore: boolean
}) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const boxContainerRef = useRef<HTMLDivElement>(null)
  const sectionRef = useRef<HTMLDivElement>(null)
  const [isActiveCategory, setIsActiveCategory] = useState<string>('all')
  const limit = 6
  const [article, setArticle] = React.useState<PostItem[]>([])
  const heroPost = posts.edges.map((edge) => edge)
  const [haveMorePosts, setHaveMorePosts] = React.useState<boolean>(hasLoadMore)
  const [loading, setLoading] = React.useState(false)
  const [first, setFirst] = React.useState(limit)
  const [startAt, setStartAt] = React.useState(posts?.pageInfo?.endCursor)

  const fetchMore = async (first: number, after: string) => {
    setLoading(true)
    const response = await getPostAndMorePosts(first, after)
    const { edges, pageInfo } = response
    const newPosts = edges.map((edge: any) => edge)
    setArticle([...article, ...newPosts])
    setFirst(first)
    setHaveMorePosts(pageInfo?.hasNextPage)
    setStartAt(pageInfo?.endCursor)
    setLoading(false)
  }

  React.useEffect(() => {
    if (article.length > 0) return
    setArticle(heroPost)
    setHaveMorePosts(posts?.pageInfo?.hasNextPage)
  }, [heroPost])

  const handleFilter = async (category: string) => {
    setSelectedCategory(category)
    setIsActiveCategory(category)
    const cate = await AllByCategory(category, 9)
    // console.log('port', cate)
    setArticle(cate?.edges ?? [])
    setHaveMorePosts(cate?.pageInfo?.hasNextPage)
    if (category === 'all') {
      setArticle(posts?.edges)
      setHaveMorePosts(posts?.pageInfo?.hasNextPage)
    }
  }

  useEffect(() => {
    const boxContainer: HTMLElement[] = gsap.utils.toArray(
      boxContainerRef?.current?.querySelectorAll('.box') ?? [],
    )
    const section = sectionRef.current

    const showThese = boxContainer.filter(
      (item) => selectedCategory === 'all' || item.classList.contains(selectedCategory),
    )

    const sectionHeight = section?.clientHeight

    const tl = new TimelineLite()
    tl.to(boxContainer, { scale: 0, opacity: 0, ease: Power3.easeOut })
    tl.set(boxContainer, { display: 'none' })
    tl.set(showThese, { display: 'block' })
    tl.to(showThese, { scale: 1, opacity: 1, ease: Power3.easeOut }, '+=0.1')
    tl.fromTo(
      section,
      { height: sectionHeight, ease: Power3.easeOut },
      { height: 'initial', ease: Power3.easeIn },
      '-=1',
    )
  }, [selectedCategory])

  const checkIsActive = (category: string) => {
    return isActiveCategory === category ? 'text-primary' : 'text-black'
  }

  const categoryItemsClass = 'font-semibold uppercase'

  return (
    <LayoutDefault image='https://twinsynergy.co.th/images/social-portfolios.webp'>
      <SectionHeader
        title='portfolio'
        bgColors='#1990FF'
        bgImage='/images/default_banner_header.webp'
        bgImageSm='/images/default_banner_header-sm.webp'
        bgImageWidth={1214}
        bgImageHeight={534}
      />
      <div className='bg-portfolio-base pb-24'>
        <div className='container mx-auto flex max-w-xs items-center justify-between py-8 md:max-w-md'>
          <button
            className={classNames(categoryItemsClass, checkIsActive('all'))}
            onClick={() => handleFilter('all')}
          >
            All
          </button>
          {allCategory?.children?.nodes?.map((category: PortfolioCategoryItem) => {
            const { id, name, slug } = category
            return (
              <button
                key={id}
                className={classNames(categoryItemsClass, checkIsActive(slug))}
                onClick={() => handleFilter(slug)}
              >
                {name}
              </button>
            )
          })}
        </div>
        <div
          ref={boxContainerRef}
          className='grid w-full grid-cols-1 flex-wrap items-start justify-center md:grid-cols-3'
        >
          {article.map((post) => {
            const { databaseId, title, slug, featuredImage, categories } = post.node
            const taxonomies = categories?.edges ?? []
            const cate = taxonomies.map(
              (category: Category) => category?.node && category?.node.name,
            )

            const cateKey = taxonomies.map(
              (category: Category) => category?.node && category?.node.slug,
            )

            return (
              <PortfolioGridItem
                key={databaseId}
                portfolioImg={featuredImage !== null ? featuredImage.node.sourceUrl : ''}
                categories={cate.filter((c) => c) as string[]}
                categoriesKey={cateKey.filter((c) => c) as string[]}
                title={title}
                slug={slug}
                portBgColor='#FFCA64'
              />
            )
          })}
        </div>
        <div ref={sectionRef} className='sort-section'>
          {/* Section content */}
        </div>
        {haveMorePosts && (
          <div className='mt-8 flex justify-center lg:mt-16'>
            <Button
              htmlType='button'
              className='btn-primary'
              disabled={loading}
              onClick={() => fetchMore(first, startAt)}
            >
              {loading ? (
                'Loading...'
              ) : (
                <>
                  โหลดเพิ่มเติม <FaArrowRight size={14} />
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </LayoutDefault>
  )
}

export const getStaticProps: GetStaticProps = async () => {
  const posts = await getAllPortfolioForHome(9, '')
  const allCategory = await AllCategory()

  return {
    props: {
      posts,
      allCategory,
      hasLoadMore: posts?.pageInfo?.hasNextPage,
    },
  }
}

export default PortfolioPage
